import React, { useState, useEffect } from 'react';
import { db } from '../../firebase/firebase';
import { collection, onSnapshot, addDoc, updateDoc, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';
import { TableTemplate, CreateTemplateData, GameType, TableStatus, GAME_TYPES, TABLE_STATUSES } from '@shared/types';
import { LoadingSpinner } from '../common/LoadingSpinner';

interface TemplateManagerProps {
  roomId: string;
  onScheduleTemplate: (template: TableTemplate) => void;
}

export function TemplateManager({ roomId, onScheduleTemplate }: TemplateManagerProps) {
  const [templates, setTemplates] = useState<TableTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<TableTemplate | null>(null);

  useEffect(() => {
    if (!roomId || !db) {
      setLoading(false);
      return;
    }

    const templatesRef = collection(db, 'rooms', roomId, 'table_templates');
    const unsubscribe = onSnapshot(
      templatesRef,
      (snapshot) => {
        const data = snapshot.docs.map(d => ({
          id: d.id,
          ...d.data(),
        })) as TableTemplate[];
        setTemplates(data);
        setLoading(false);
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [roomId]);

  const handleCreateTemplate = async (data: CreateTemplateData) => {
    try {
      setActionLoading(true);
      setError(null);
      const templatesRef = collection(db, 'rooms', roomId, 'table_templates');
      await addDoc(templatesRef, {
        ...data,
        room_id: roomId,
        created_at: serverTimestamp(),
        updated_at: serverTimestamp(),
      });
      setShowCreateForm(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create template');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateTemplate = async (templateId: string, data: CreateTemplateData) => {
    try {
      setActionLoading(true);
      setError(null);
      const templateRef = doc(db, 'rooms', roomId, 'table_templates', templateId);
      await updateDoc(templateRef, {
        ...data,
        updated_at: serverTimestamp(),
      });
      setEditingTemplate(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update template');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteTemplate = async (templateId: string) => {
    if (!confirm('Are you sure you want to delete this template? Any scheduled tables using this template will fail when they execute.')) return;
    try {
      setActionLoading(true);
      setError(null);
      const templateRef = doc(db, 'rooms', roomId, 'table_templates', templateId);
      await deleteDoc(templateRef);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete template');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'Open': return 'bg-green-100 text-green-800';
      case 'Running': return 'bg-blue-100 text-blue-800';
      case 'Closed': return 'bg-gray-100 text-gray-800';
      case 'Interest': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="card">
        <div className="flex items-center justify-center py-8">
          <LoadingSpinner size="md" />
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Table Templates</h2>
          <p className="text-sm text-gray-500 mt-1">Reusable configurations for quick table creation and scheduling</p>
        </div>
        <button
          onClick={() => setShowCreateForm(true)}
          className="btn btn-primary"
          disabled={actionLoading}
        >
          {actionLoading ? <LoadingSpinner size="sm" className="mr-2" /> : (
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          )}
          Create Template
        </button>
      </div>

      {error && (
        <div className="mb-4 rounded-md bg-red-50 p-4">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {showCreateForm && (
        <TemplateForm
          onSubmit={handleCreateTemplate}
          onCancel={() => setShowCreateForm(false)}
          loading={actionLoading}
          title="Create New Template"
          submitText="Create Template"
        />
      )}

      {editingTemplate && (
        <TemplateForm
          initialData={{
            name: editingTemplate.name,
            game_type: editingTemplate.game_type,
            custom_game_type: editingTemplate.custom_game_type,
            stakes: editingTemplate.stakes,
            number_of_tables: editingTemplate.number_of_tables,
            status: editingTemplate.status,
            waitlist_limit: editingTemplate.waitlist_limit,
          }}
          onSubmit={(data) => handleUpdateTemplate(editingTemplate.id, data)}
          onCancel={() => setEditingTemplate(null)}
          loading={actionLoading}
          title="Edit Template"
          submitText="Update Template"
        />
      )}

      {templates.length === 0 ? (
        <div className="text-center py-8">
          <svg className="w-12 h-12 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
          </svg>
          <p className="text-gray-500">No templates created yet.</p>
          <p className="text-sm text-gray-400 mt-1">Create a template to quickly add and schedule tables.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map((template) => (
            <div key={template.id} className="card card-hover">
              <div className="flex items-start justify-between mb-3">
                <h3 className="font-semibold text-gray-900 truncate pr-2">{template.name}</h3>
                <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium uppercase tracking-wide ${getStatusBadge(template.status)}`}>
                  {template.status}
                </span>
              </div>

              <div className="space-y-1.5 text-sm text-gray-600 mb-4">
                <p><span className="font-medium">Game:</span> {template.game_type}{template.custom_game_type ? ` (${template.custom_game_type})` : ''}</p>
                <p><span className="font-medium">Stakes:</span> {template.stakes}</p>
                <p><span className="font-medium">Tables:</span> {template.number_of_tables}</p>
              </div>

              <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setEditingTemplate(template)}
                    className="btn btn-outline btn-sm"
                    disabled={actionLoading}
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDeleteTemplate(template.id)}
                    className="btn btn-danger btn-sm"
                    disabled={actionLoading}
                  >
                    Delete
                  </button>
                </div>
                <button
                  onClick={() => onScheduleTemplate(template)}
                  className="btn btn-primary btn-sm"
                  disabled={actionLoading}
                >
                  <svg className="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Schedule
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ===== Template Form (shared for create/edit) =====

interface TemplateFormProps {
  initialData?: CreateTemplateData;
  onSubmit: (data: CreateTemplateData) => void;
  onCancel: () => void;
  loading: boolean;
  title: string;
  submitText: string;
}

function TemplateForm({ initialData, onSubmit, onCancel, loading, title, submitText }: TemplateFormProps) {
  const [formData, setFormData] = useState<CreateTemplateData>({
    name: initialData?.name || '',
    game_type: initialData?.game_type || 'NLH',
    custom_game_type: initialData?.custom_game_type || '',
    stakes: initialData?.stakes || '$1/$3',
    number_of_tables: initialData?.number_of_tables ?? 1,
    status: initialData?.status || 'Open',
    waitlist_limit: initialData?.waitlist_limit,
  });

  const handleStatusChange = (newStatus: TableStatus) => {
    const updated = { ...formData, status: newStatus };
    if (newStatus === 'Interest' || newStatus === 'Closed') {
      updated.number_of_tables = 0;
    } else if ((newStatus === 'Open' || newStatus === 'Running') && updated.number_of_tables === 0) {
      updated.number_of_tables = 1;
    }
    setFormData(updated);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const submitData: CreateTemplateData = { ...formData };
    if (formData.game_type !== 'Custom') {
      delete submitData.custom_game_type;
    }
    if (formData.waitlist_limit === undefined || formData.waitlist_limit === null) {
      delete submitData.waitlist_limit;
    }
    onSubmit(submitData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl p-6 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="label">Template Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="input"
              placeholder="e.g., Friday $1/$3 NLH"
              required
            />
            <p className="text-xs text-gray-400 mt-1">This will also be the default table name when used</p>
          </div>

          <div>
            <label className="label">Game Type</label>
            <select
              value={formData.game_type}
              onChange={(e) => setFormData({ ...formData, game_type: e.target.value as GameType })}
              className="input"
            >
              {GAME_TYPES.map((type) => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>

          {formData.game_type === 'Custom' && (
            <div>
              <label className="label">Custom Game Type</label>
              <input
                type="text"
                value={formData.custom_game_type || ''}
                onChange={(e) => setFormData({ ...formData, custom_game_type: e.target.value })}
                className="input"
                placeholder="e.g., Big O"
                required
              />
            </div>
          )}

          <div>
            <label className="label">Stakes</label>
            <input
              type="text"
              value={formData.stakes}
              onChange={(e) => setFormData({ ...formData, stakes: e.target.value })}
              className="input"
              placeholder="e.g., $1/$3"
              required
            />
          </div>

          <div>
            <label className="label">Default Status</label>
            <select
              value={formData.status}
              onChange={(e) => handleStatusChange(e.target.value as TableStatus)}
              className="input"
            >
              {TABLE_STATUSES.map((status) => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
          </div>

          {(formData.status === 'Open' || formData.status === 'Running') && (
            <div>
              <label className="label">Number of Tables</label>
              <input
                type="number"
                min="1"
                max="50"
                value={formData.number_of_tables}
                onChange={(e) => setFormData({ ...formData, number_of_tables: parseInt(e.target.value) || 1 })}
                className="input"
                required
              />
            </div>
          )}

          {(formData.status === 'Interest' || formData.status === 'Closed') && (
            <div>
              <label className="label">Number of Tables</label>
              <input type="number" min="0" max="0" value="0" disabled className="input bg-gray-100" />
              <p className="text-sm text-gray-500 mt-1">
                Tables are automatically set to 0 for {formData.status.toLowerCase()} status
              </p>
            </div>
          )}

          <div className="flex items-center justify-end space-x-3 pt-4">
            <button type="button" onClick={onCancel} className="btn btn-outline" disabled={loading}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? <LoadingSpinner size="sm" className="mr-2" /> : null}
              {submitText}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
